"use client";

import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { CATEGORIES, COLLECTIONS, PRODUCTS, cutout, minPrice, rub, type Category, type Product } from "@/lib/catalog";
import { L as Link, useDesign } from "@/components/design";
import { cart } from "../cart";
import ProductCard from "./ProductCard";
import { favs } from "./sets";

const FIRST = 12;
const STEP = 8;
type Sort = "popular" | "cheap" | "expensive" | "rating" | "new";
const BY: Record<Sort, (a: Product, b: Product) => number> = {
  popular: (a, b) => a.popular - b.popular,
  cheap: (a, b) => minPrice(a) - minPrice(b),
  expensive: (a, b) => minPrice(b) - minPrice(a),
  rating: (a, b) => b.rating - a.rating || b.ratingCount - a.ratingCount,
  new: (a, b) => a.added - b.added,
};

// Каталог: подборки, товар дня, фильтры (раздел, избранное, цена, скидка), «Показать ещё».
// only — список slug-ов для страницы подборки.
export default function CatalogView({ only, current }: { only?: string[]; current?: string }) {
  const [cat, setCat] = useState<Category | "all" | "fav">("all");
  const [sort, setSort] = useState<Sort>("popular");
  const [onSale, setOnSale] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [shown, setShown] = useState(FIRST);
  const fav = favs.use();

  const base = useMemo(() => (only ? PRODUCTS.filter((p) => only.includes(p.slug)) : PRODUCTS), [only]);
  const list = useMemo(() => {
    const lo = Number(from) || 0;
    const hi = Number(to) || Infinity;
    const items = base.filter(
      (p) =>
        (cat === "all" || (cat === "fav" ? fav.includes(p.slug) : p.category === cat)) &&
        (!onSale || p.variants.some((v) => v.oldPrice)) &&
        p.variants.some((v) => v.price >= lo && v.price <= hi)
    );
    return [...items].sort(BY[sort]);
  }, [base, cat, sort, onSale, from, to, fav]);

  useEffect(() => setShown(FIRST), [cat, sort, onSale, from, to]);

  const count = (c: Category | "all") => base.filter((p) => c === "all" || p.category === c).length;
  const cats = CATEGORIES.filter((c) => count(c.id) > 0);

  return (
    <>
      <nav className="podborki" aria-label="Подборки">
        {COLLECTIONS.filter((c) => c.slug !== current).map((c) => (
          <Link key={c.slug} className="podborka label" href={`/catalog/podborka/${c.slug}`}>
            {c.title}
          </Link>
        ))}
      </nav>
      {!only && <DealOfDay />}
      <div className="filters">
        <div className="chips" role="group" aria-label="Разделы">
          {[{ id: "all" as const, label: "Все" }, ...(only ? [] : cats)].map((c) => (
            <button key={c.id} type="button" className="chip label" aria-pressed={cat === c.id} onClick={() => setCat(c.id)}>
              {c.label} <sup>{count(c.id)}</sup>
            </button>
          ))}
          <button type="button" className="chip chip--fav label" aria-pressed={cat === "fav"} onClick={() => setCat(cat === "fav" ? "all" : "fav")}>
            ♥ Избранное <sup>{fav.filter((s) => base.some((p) => p.slug === s)).length}</sup>
          </button>
        </div>
        <div className="filters__right">
          <fieldset className="price-range label">
            <legend className="sr">Цена, ₽</legend>
            <span aria-hidden="true">Цена</span>
            <input type="number" inputMode="numeric" min={0} step={100} placeholder="от" aria-label="Цена от, ₽" value={from} onChange={(e) => setFrom(e.target.value)} />
            <span aria-hidden="true">–</span>
            <input type="number" inputMode="numeric" min={0} step={100} placeholder="до" aria-label="Цена до, ₽" value={to} onChange={(e) => setTo(e.target.value)} />
          </fieldset>
          <label className="toggle label">
            <input type="checkbox" checked={onSale} onChange={(e) => setOnSale(e.target.checked)} /> Со скидкой
          </label>
          <label className="sort label" htmlFor="sort">
            <span className="sr">Сортировка</span>
            <select id="sort" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
              <option value="popular">Популярные</option>
              <option value="rating">С высоким рейтингом</option>
              <option value="cheap">Сначала дешевле</option>
              <option value="expensive">Сначала дороже</option>
              <option value="new">Новинки</option>
            </select>
          </label>
        </div>
      </div>
      <h2 className="sr">Товары</h2>
      <p className="found label" aria-live="polite">
        Найдено: {list.length}
      </p>
      <div className="grid4 grid4--catalog">
        {list.slice(0, shown).map((p) => (
          <ProductCard key={p.slug} p={p} />
        ))}
        {list.length === 0 && (
          <p className="empty">{cat === "fav" ? "В избранном пусто. Нажмите ♥ на карточке — отложим." : "Под такие условия ничего не нашлось, как и птенцов. Ослабьте фильтры."}</p>
        )}
      </div>
      {list.length > shown && (
        <div className="more">
          <button type="button" className="btn btn-o label" onClick={() => setShown((n) => n + STEP)}>
            Показать ещё {Math.min(STEP, list.length - shown)} из {list.length - shown}
          </button>
        </div>
      )}
    </>
  );
}

// «Товар дня»: выбирается по дате в браузере (страница статическая), таймер — до полуночи.
const DEALS = PRODUCTS.filter((p) => p.variants.some((v) => v.oldPrice));
function DealOfDay() {
  const [now, setNow] = useState<Date | null>(null);
  const [added, setAdded] = useState(false);
  const { design } = useDesign();
  useEffect(() => {
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!now) return <div className="deal deal--wait" aria-hidden="true" />;
  const day = Math.floor((now.getTime() - now.getTimezoneOffset() * 60000) / 86400000);
  const p = DEALS[day % DEALS.length];
  const v = p.variants.find((x) => x.oldPrice)!;
  const left = Math.max(0, Math.floor((new Date(now).setHours(24, 0, 0, 0) - now.getTime()) / 1000));
  const hh = String(Math.floor(left / 3600)).padStart(2, "0");
  const mm = String(Math.floor((left % 3600) / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <section className="deal" aria-labelledby="deal-title">
      <Link href={`/catalog/${p.slug}`} className="deal__img" tabIndex={-1} aria-hidden="true">
        <Image src={design === "gallery" ? cutout(p) : p.images[0].src} alt="" width={600} height={600} sizes="(max-width: 700px) 40vw, 220px" />
      </Link>
      <div className="deal__copy">
        <p className="label deal__tag">Товар дня · −{Math.round((1 - v.price / v.oldPrice!) * 100)}%</p>
        <h2 id="deal-title">
          <Link href={`/catalog/${p.slug}`}>{p.name}</Link>
        </h2>
        <p className="deal__note">
          {p.variants.length > 1 ? `${v.name} · ` : ""}
          {p.subtitle}
        </p>
      </div>
      <div className="deal__buy">
        <p className="deal__timer label" role="timer" aria-label={`До конца акции ${hh} ч ${mm} мин`}>
          До конца дня <b>{hh}:{mm}:{ss}</b>
        </p>
        <p className="price">
          {rub(v.price)} <s>{rub(v.oldPrice!)}</s>
        </p>
        <button
          type="button"
          className={`add label${added ? " done" : ""}`}
          onClick={() => {
            cart.add(p.slug, v.name);
            setAdded(true);
          }}
        >
          {added ? "Добавлено" : "В корзину"}
        </button>
      </div>
    </section>
  );
}
